import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { woocommerceStoreService } from "../services/woocommerceStoreService";
import type { WooOrder, WooStoreConfig } from "../types";
import { isHistoryOrder, isPendingOrder } from "../utils/orderHelpers";

function mapOrdersError(err: unknown): string {
  return err instanceof Error
    ? err.message
    : "Não foi possível carregar seus pedidos.";
}

function sortOrdersDesc(orders: WooOrder[]): WooOrder[] {
  return [...orders].sort(
    (a, b) =>
      new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime(),
  );
}

/** Pedidos WooCommerce agregados de todas as lojas ativas. */
export function useWooCommerceOrdersAllStores(
  activeStores: WooStoreConfig[],
  userEmail: string,
) {
  const [orders, setOrders] = useState<WooOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolveStore = useCallback(
    (order: WooOrder): WooStoreConfig | null =>
      activeStores.find((store) => store.id === order.storeId) ?? null,
    [activeStores],
  );

  const loadAllOrders = useCallback(async () => {
    const normalizedEmail = userEmail.trim().toLowerCase();
    if (!normalizedEmail || activeStores.length === 0) {
      setOrders([]);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const results = await Promise.allSettled(
        activeStores.map(async (store) => {
          const customer = await woocommerceStoreService.findCustomerByEmail(
            store,
            normalizedEmail,
          );
          if (!customer) return [];
          return woocommerceStoreService.listOrdersByCustomerWithEscalatingWindow(
            store,
            customer.id,
          );
        }),
      );

      const merged: WooOrder[] = [];
      for (const result of results) {
        if (result.status === "fulfilled") {
          merged.push(...result.value);
        }
      }

      const sorted = sortOrdersDesc(merged);
      setOrders(sorted);
      return sorted;
    } catch (err) {
      const message = mapOrdersError(err);
      setError(message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [activeStores, userEmail]);

  const refreshAllOrders = loadAllOrders;

  const cancelOrder = useCallback(
    async (order: WooOrder) => {
      const store = resolveStore(order);
      if (!store) {
        toast.error("Dispensador do pedido não encontrado.");
        return;
      }

      let previousOrders: WooOrder[] = [];
      setOrders((prev) => {
        previousOrders = prev;
        return prev.map((entry) =>
          entry.id === order.id
            ? {
                ...entry,
                status: "cancelled",
                needsPayment: false,
              }
            : entry,
        );
      });

      try {
        await woocommerceStoreService.updateOrder(store, order.id, {
          status: "cancelled",
        });
        toast.success("Pedido cancelado.");
        void loadAllOrders();
      } catch (err) {
        setOrders(previousOrders);
        toast.error(
          err instanceof Error
            ? err.message
            : "Não foi possível cancelar o pedido.",
        );
      }
    },
    [resolveStore, loadAllOrders],
  );

  const pendingOrders = useMemo(
    () => orders.filter(isPendingOrder),
    [orders],
  );

  const orderHistory = useMemo(
    () => orders.filter(isHistoryOrder),
    [orders],
  );

  const unpaidCount = pendingOrders.length;

  return {
    orders,
    pendingOrders,
    orderHistory,
    unpaidCount,
    loadAllOrders,
    refreshAllOrders,
    cancelOrder,
    resolveStore,
    loading,
    error,
  };
}
